import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  Trash2,
  Building2,
  UserCheck,
  Users,
  Copy,
  Key,
  Mail,
  MapPin,
  Filter,
  X,
  Map,
  Building,
  UsersRound,
  FolderKanban,
  Pencil,
  Trash2 as TrashIcon,
  Eye,
  Phone,
  MessageCircle,
  User,
  AlertTriangle,
  Send,
  FileText,
  Trophy,
  CheckCircle2,
  BarChart3
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOGO_REGIONS = ["Maritime", "Plateaux", "Centrale", "Kara", "Savanes", "DAGL"];

const SECTORS = [
  "Agriculture",
  "Agroalimentaire",
  "Artisanat",
  "Commerce",
  "Construction/BTP",
  "Éducation/Formation",
  "Industrie",
  "Informatique/Tech",
  "Santé",
  "Services",
  "Tourisme/Hôtellerie",
  "Transport/Logistique",
  "Autre"
];

export const Companies = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [reportStatus, setReportStatus] = useState({});  // Track report status per assignment
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState({ email: '', password: '' });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportingCompany, setReportingCompany] = useState(null);
  const [reportForm, setReportForm] = useState({ report_type: '', description: '' });
  const [submittingReport, setSubmittingReport] = useState(false);
  // Digitalization index state
  const [digitalizationDialogOpen, setDigitalizationDialogOpen] = useState(false);
  const [digitalizationCompany, setDigitalizationCompany] = useState(null);
  const [digitalizationForm, setDigitalizationForm] = useState({ index_type: 'initial', value: '' });
  const [submittingDigitalization, setSubmittingDigitalization] = useState(false);

const REPORT_TYPES = ["Injoignable", "Faillite", "Déménagement", "Fermeture", "Changement de contact", "Autre"];
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedConsultantId, setSelectedConsultantId] = useState('');
  const [consultantSearch, setConsultantSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    region: '',
    hasConsultant: '',
    sector: ''
  });
  
  const [newCompany, setNewCompany] = useState({
    name: '',
    description: '',
    address: '',
    region: '',
    prefecture: '',
    commune: '',
    sector: '',
    employee_count: '',
    geolocation: { latitude: '', longitude: '' },
    contact_person: '',
    contact_gender: '',
    contact_email: '',
    contact_phone: '',
    contact_whatsapp: '',
    user_email: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (filters.region) params.append('region', filters.region);
      if (filters.hasConsultant !== '') params.append('has_consultant', filters.hasConsultant);
      if (filters.sector) params.append('sector', filters.sector);
      if (searchTerm) params.append('search', searchTerm);
      
      const companiesEndpoint = user?.role === 'admin' 
        ? `sme-companies/all?${params.toString()}` 
        : 'sme-companies';
      
      const [companiesRes] = await Promise.all([
        axios.get(`${API}/${companiesEndpoint}`)
      ]);
      setCompanies(companiesRes.data);
      
      // Fetch consultants, projects, assignments and stats for admin
      if (user?.role === 'admin') {
        const [consultantsRes, statsRes, projectsRes, assignmentsRes] = await Promise.all([
          axios.get(`${API}/users/consultants`),
          axios.get(`${API}/sme-companies/stats`),
          axios.get(`${API}/projects`),
          axios.get(`${API}/assignments`)
        ]);
        setConsultants(consultantsRes.data);
        setStats(statsRes.data);
        setProjects(projectsRes.data.filter(p => p.status === 'active' || p.status === 'draft'));
        setAssignments(assignmentsRes.data);
      }
      
      // For consultants, check report status for each company's assignment
      if (user?.role === 'consultant') {
        const statusMap = {};
        for (const company of companiesRes.data) {
          if (company.assignment_id) {
            try {
              const res = await axios.get(`${API}/assignments/${company.assignment_id}/can-submit-report`);
              statusMap[company.assignment_id] = res.data;
            } catch (e) {
              // Ignore errors
            }
          }
        }
        setReportStatus(statusMap);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get consultant assignment for a company (from assignments, not direct)
  const getCompanyAssignment = (companyId) => {
    // Find the most recent active assignment for this company
    const companyAssignments = assignments
      .filter(a => a.sme_company_id === companyId && a.status === 'active')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return companyAssignments.length > 0 ? companyAssignments[0] : null;
  };

  const handleSearch = () => {
    fetchData();
  };

  const resetFilters = () => {
    setFilters({ region: '', hasConsultant: '', sector: '' });
    setSearchTerm('');
  };

  const handleCreate = async () => {
    if (!newCompany.name.trim()) {
      toast.error('Le nom de l\'entreprise est requis');
      return;
    }
    if (!newCompany.user_email.trim()) {
      toast.error('L\'email du compte PME est requis');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...newCompany,
        employee_count: newCompany.employee_count ? parseInt(newCompany.employee_count) : null,
        geolocation: (newCompany.geolocation.latitude && newCompany.geolocation.longitude) 
          ? {
              latitude: parseFloat(newCompany.geolocation.latitude),
              longitude: parseFloat(newCompany.geolocation.longitude)
            }
          : null
      };
      
      const response = await axios.post(`${API}/sme-companies`, payload);
      const { generated_password } = response.data;
      
      setGeneratedCredentials({
        email: newCompany.user_email,
        password: generated_password,
        companyName: newCompany.name
      });
      
      setDialogOpen(false);
      setNewCompany({
        name: '',
        address: '',
        region: '',
        prefecture: '',
        commune: '',
        sector: '',
        employee_count: '',
        geolocation: { latitude: '', longitude: '' },
        contact_person: '',
        contact_email: '',
        contact_phone: '',
        user_email: ''
      });
      
      setCredentialsDialogOpen(true);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'ajout';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/sme-companies/${id}`);
      toast.success('Entreprise supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openAssignDialog = (company) => {
    setSelectedCompany(company);
    // Check if there's an existing assignment for this company
    const existingAssignment = getCompanyAssignment(company.id);
    if (existingAssignment) {
      setSelectedProjectId(existingAssignment.project_id);
      setSelectedConsultantId(existingAssignment.consultant_id);
    } else {
      setSelectedProjectId('');
      setSelectedConsultantId('');
    }
    setConsultantSearch('');
    setAssignDialogOpen(true);
  };

  const handleAssignConsultant = async () => {
    if (!selectedCompany || !selectedProjectId || !selectedConsultantId) {
      toast.error('Veuillez sélectionner un projet et un consultant');
      return;
    }

    setSaving(true);
    try {
      // Check if assignment already exists for this project-consultant-company combination
      const existingAssignment = assignments.find(
        a => a.project_id === selectedProjectId && 
             a.consultant_id === selectedConsultantId && 
             a.sme_company_id === selectedCompany.id
      );
      
      if (existingAssignment) {
        toast.info('Cette affectation existe déjà');
        setAssignDialogOpen(false);
        return;
      }
      
      // Create new assignment
      await axios.post(`${API}/assignments`, {
        project_id: selectedProjectId,
        consultant_id: selectedConsultantId,
        sme_company_id: selectedCompany.id,
        min_hours: 0,
        min_interventions: 0
      });

      toast.success('Consultant attribué avec succès');
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'attribution';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Delete an assignment
  const handleDeleteAssignment = async (assignmentId) => {
    try {
      await axios.delete(`${API}/assignments/${assignmentId}`);
      toast.success('Affectation supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Delete assignment by consultant (uses different endpoint)
  const handleConsultantDeleteAssignment = async (companyId) => {
    try {
      await axios.delete(`${API}/consultant/assignments/${companyId}`);
      toast.success('Attribution supprimée avec succès');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Open company details modal
  const openDetailsDialog = (company) => {
    setViewingCompany(company);
    setDetailsDialogOpen(true);
  };

  // Open report dialog
  const openReportDialog = (company) => {
    setReportingCompany(company);
    setReportForm({ report_type: '', description: '' });
    setReportDialogOpen(true);
  };

  // Submit company report
  const handleSubmitReport = async () => {
    if (!reportForm.report_type) {
      toast.error('Veuillez sélectionner un type de signalement');
      return;
    }
    if (!reportForm.description.trim()) {
      toast.error('Veuillez décrire le problème');
      return;
    }

    setSubmittingReport(true);
    try {
      await axios.post(`${API}/company-reports`, {
        company_id: reportingCompany.id,
        report_type: reportForm.report_type,
        description: reportForm.description
      });
      toast.success('Signalement envoyé avec succès');
      setReportDialogOpen(false);
      setReportForm({ report_type: '', description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du signalement');
    } finally {
      setSubmittingReport(false);
    }
  };

  // Open digitalization dialog
  const openDigitalizationDialog = (company) => {
    setDigitalizationCompany(company);
    // Pre-select type based on existing data
    if (company.initial_digitalization_index === null || company.initial_digitalization_index === undefined) {
      setDigitalizationForm({ index_type: 'initial', value: '' });
    } else if (company.final_digitalization_index === null || company.final_digitalization_index === undefined) {
      setDigitalizationForm({ index_type: 'final', value: '' });
    } else {
      // Both exist, default to final for update
      setDigitalizationForm({ index_type: 'final', value: '' });
    }
    setDigitalizationDialogOpen(true);
  };

  // Submit digitalization index
  const handleSubmitDigitalization = async () => {
    const value = parseFloat(digitalizationForm.value);
    if (isNaN(value) || value < 0 || value > 100) {
      toast.error('L\'indice doit être compris entre 0 et 100');
      return;
    }

    setSubmittingDigitalization(true);
    try {
      await axios.put(`${API}/sme-companies/${digitalizationCompany.id}/digitalization-index`, {
        index_type: digitalizationForm.index_type,
        value: value
      });
      toast.success(`Indice ${digitalizationForm.index_type === 'initial' ? 'initial' : 'final'} enregistré avec succès`);
      setDigitalizationDialogOpen(false);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmittingDigitalization(false);
    }
  };

  // Filter consultants by search
  const filteredConsultants = consultants.filter(c => 
    c.name.toLowerCase().includes(consultantSearch.toLowerCase()) ||
    c.email.toLowerCase().includes(consultantSearch.toLowerCase())
  );

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.contact_person && company.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const activeFiltersCount = [filters.region, filters.hasConsultant, filters.sector].filter(Boolean).length;

  if (loading && companies.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="companies-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              {user?.role === 'consultant' ? 'Mes PME attribuées' : 'Entreprises (PME)'}
            </h1>
            <p className="text-slate-500 mt-1">
              {user?.role === 'consultant' 
                ? 'PME dont vous êtes responsable' 
                : 'Gérez les entreprises clientes et leurs attributions'}
            </p>
          </div>
          {user?.role === 'admin' && (
            <Button 
              onClick={() => setDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white" 
              data-testid="add-company-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une entreprise
            </Button>
          )}
        </div>

        {/* Stats Cards (Admin only) */}
        {user?.role === 'admin' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    <p className="text-sm text-slate-500">Total PME</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <UserCheck className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.with_consultant}</p>
                    <p className="text-sm text-slate-500">Avec consultant</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Users className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.without_consultant}</p>
                    <p className="text-sm text-slate-500">Sans consultant</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MapPin className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{Object.keys(stats.by_region || {}).length}</p>
                    <p className="text-sm text-slate-500">Régions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info for consultant */}
        {user?.role === 'consultant' && companies.length === 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4 flex items-center gap-3">
              <Building2 className="w-5 h-5 text-orange-500" />
              <p className="text-orange-800">
                Aucune PME ne vous a été attribuée. Contactez votre administrateur pour obtenir des attributions.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Search and Filters */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, contact..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              {user?.role === 'admin' && (
                <Button
                  variant={showFilters ? "secondary" : "outline"}
                  onClick={() => setShowFilters(!showFilters)}
                  className="relative"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filtres
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              )}
            </div>

            {/* Filters Panel */}
            {showFilters && user?.role === 'admin' && (
              <div className="pt-4 border-t border-slate-200 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Région</Label>
                    <Select value={filters.region || "all"} onValueChange={(v) => setFilters({...filters, region: v === "all" ? "" : v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Toutes les régions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les régions</SelectItem>
                        {TOGO_REGIONS.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Consultant attribué</Label>
                    <Select value={filters.hasConsultant || "all"} onValueChange={(v) => setFilters({...filters, hasConsultant: v === "all" ? "" : v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="true">Avec consultant</SelectItem>
                        <SelectItem value="false">Sans consultant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Secteur d'activité</Label>
                    <Select value={filters.sector || "all"} onValueChange={(v) => setFilters({...filters, sector: v === "all" ? "" : v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tous les secteurs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les secteurs</SelectItem>
                        {SECTORS.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={resetFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Réinitialiser
                  </Button>
                  <Button size="sm" onClick={handleSearch}>
                    Appliquer
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Companies Table */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {filteredCompanies.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {companies.length === 0 
                    ? (user?.role === 'consultant' ? 'Aucune PME attribuée' : 'Aucune entreprise')
                    : 'Aucune entreprise ne correspond à vos critères'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-slate-500 font-medium">Nom</TableHead>
                      <TableHead className="text-slate-500 font-medium">Région</TableHead>
                      <TableHead className="text-slate-500 font-medium">Contact</TableHead>
                      <TableHead className="text-slate-500 font-medium">Téléphone</TableHead>
                      {user?.role === 'admin' && (
                        <TableHead className="text-slate-500 font-medium">Consultant</TableHead>
                      )}
                      <TableHead className="text-slate-500 font-medium text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompanies.map((company) => (
                      <TableRow key={company.id} data-testid={`row-${company.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{company.name}</p>
                            {company.sector && (
                              <p className="text-xs text-slate-500">{company.sector}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {company.region ? (
                            <div>
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                <MapPin className="w-3 h-3 mr-1" />
                                {company.region}
                              </Badge>
                              {company.prefecture && (
                                <p className="text-xs text-slate-500 mt-1">{company.prefecture}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-slate-600">{company.contact_person || '-'}</p>
                            {company.contact_email && (
                              <p className="text-xs text-slate-400">{company.contact_email}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {company.contact_phone ? (
                              <div className="flex items-center gap-1 text-sm text-slate-600">
                                <Phone className="w-3 h-3 text-slate-400" />
                                <span>{company.contact_phone}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                            {company.contact_whatsapp && (
                              <div className="flex items-center gap-1 text-xs text-green-600">
                                <MessageCircle className="w-3 h-3" />
                                <span>{company.contact_whatsapp}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        {user?.role === 'admin' && (
                          <TableCell>
                            {(() => {
                              const assignment = getCompanyAssignment(company.id);
                              if (assignment) {
                                return (
                                  <div className="flex items-center gap-2">
                                    <div className="space-y-1 flex-1">
                                      <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                                        <UserCheck className="w-3 h-3 mr-1" />
                                        {assignment.consultant_name}
                                      </Badge>
                                      <p className="text-xs text-slate-500 flex items-center gap-1">
                                        <FolderKanban className="w-3 h-3" />
                                        {assignment.project_name}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                                        onClick={() => openAssignDialog(company)}
                                        title="Modifier l'affectation"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </Button>
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
                                            title="Supprimer l'affectation"
                                          >
                                            <TrashIcon className="w-3.5 h-3.5" />
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>Supprimer cette affectation ?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                              Le consultant <strong>{assignment.consultant_name}</strong> ne sera plus assigné 
                                              à <strong>{company.name}</strong> pour le projet <strong>{assignment.project_name}</strong>.
                                              Les interventions existantes ne seront pas supprimées.
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                                            <AlertDialogAction
                                              onClick={() => handleDeleteAssignment(assignment.id)}
                                              className="bg-red-600 hover:bg-red-700"
                                            >
                                              Supprimer
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">
                                  Non attribué
                                </Badge>
                              );
                            })()}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Consultant Actions */}
                            {user?.role === 'consultant' && (
                              <>
                                {/* Final Report Button - Always show to track progress */}
                                {company.assignment_id && reportStatus[company.assignment_id] && (
                                  reportStatus[company.assignment_id].reason === 'already_submitted' ? (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => navigate(`/consultant/assignment/${company.assignment_id}/final-report`)}
                                      className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                      data-testid={`final-report-completed-${company.id}`}
                                    >
                                      <Trophy className="w-4 h-4 mr-1" />
                                      Mission Terminée
                                    </Button>
                                  ) : reportStatus[company.assignment_id].can_submit ? (
                                    <Button 
                                      size="sm"
                                      onClick={() => navigate(`/consultant/assignment/${company.assignment_id}/final-report`)}
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                      data-testid={`final-report-ready-${company.id}`}
                                    >
                                      <FileText className="w-4 h-4 mr-1" />
                                      Soumettre Rapport
                                    </Button>
                                  ) : (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => navigate(`/consultant/assignment/${company.assignment_id}/final-report`)}
                                      className="text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                      data-testid={`final-report-progress-${company.id}`}
                                    >
                                      <FileText className="w-4 h-4 mr-1" />
                                      Progression
                                    </Button>
                                  )
                                )}
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => openDetailsDialog(company)}
                                  data-testid={`view-details-${company.id}`}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Détails
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => openReportDialog(company)}
                                  data-testid={`report-${company.id}`}
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                >
                                  <AlertTriangle className="w-4 h-4 mr-1" />
                                  Signaler
                                </Button>
                                {/* Digitalization Index Button */}
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => openDigitalizationDialog(company)}
                                  data-testid={`digitalization-${company.id}`}
                                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                >
                                  <BarChart3 className="w-4 h-4 mr-1" />
                                  {company.initial_digitalization_index !== null && company.initial_digitalization_index !== undefined ? (
                                    company.final_digitalization_index !== null && company.final_digitalization_index !== undefined ? 
                                      `${company.initial_digitalization_index}→${company.final_digitalization_index}` : 
                                      `Initial: ${company.initial_digitalization_index}`
                                  ) : 'Indice'}
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`remove-assignment-${company.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Supprimer cette attribution ?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Vous ne serez plus assigné à <strong>{company.name}</strong>. 
                                        Les interventions existantes seront conservées mais vous ne pourrez plus en créer de nouvelles pour cette PME.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleConsultantDeleteAssignment(company.id)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Supprimer
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                            {/* Admin Actions */}
                            {user?.role === 'admin' && !getCompanyAssignment(company.id) && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openAssignDialog(company)}
                                data-testid={`assign-${company.id}`}
                              >
                                <Users className="w-4 h-4 mr-1" />
                                Attribuer
                              </Button>
                            )}
                            {user?.role === 'admin' && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-${company.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Supprimer l'entreprise ?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Cette action est irréversible. L'entreprise et toutes ses données associées seront supprimées.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleDelete(company.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Supprimer
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Company Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Nouvelle entreprise</DialogTitle>
              <DialogDescription>
                Ajoutez une nouvelle PME et créez son compte utilisateur
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
              {/* Company Info */}
              <div className="space-y-2">
                <Label>Nom de l'entreprise *</Label>
                <Input
                  placeholder="Nom de l'entreprise"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                  data-testid="company-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <textarea
                  placeholder="Décrivez brièvement l'entreprise, ses activités..."
                  value={newCompany.description}
                  onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-orange-500" />
                  Email du compte PME *
                </Label>
                <Input
                  type="email"
                  placeholder="utilisateur@pme.com"
                  value={newCompany.user_email}
                  onChange={(e) => setNewCompany({ ...newCompany, user_email: e.target.value })}
                  data-testid="user-email-input"
                />
                <p className="text-xs text-slate-500">
                  Cet email sera utilisé pour la connexion
                </p>
              </div>

              {/* Location */}
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Localisation
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Région</Label>
                    <Select 
                      value={newCompany.region} 
                      onValueChange={(v) => setNewCompany({...newCompany, region: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {TOGO_REGIONS.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Préfecture</Label>
                    <Input
                      placeholder="Préfecture"
                      value={newCompany.prefecture}
                      onChange={(e) => setNewCompany({ ...newCompany, prefecture: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-2">
                    <Label>Commune</Label>
                    <Input
                      placeholder="Commune"
                      value={newCompany.commune}
                      onChange={(e) => setNewCompany({ ...newCompany, commune: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Adresse</Label>
                    <Input
                      placeholder="Adresse complète"
                      value={newCompany.address}
                      onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Geolocation */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Map className="w-4 h-4 text-slate-500" />
                  Géolocalisation (optionnel)
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="number"
                    step="any"
                    placeholder="Latitude (ex: 6.1319)"
                    value={newCompany.geolocation.latitude}
                    onChange={(e) => setNewCompany({ 
                      ...newCompany, 
                      geolocation: { ...newCompany.geolocation, latitude: e.target.value }
                    })}
                  />
                  <Input
                    type="number"
                    step="any"
                    placeholder="Longitude (ex: 1.2228)"
                    value={newCompany.geolocation.longitude}
                    onChange={(e) => setNewCompany({ 
                      ...newCompany, 
                      geolocation: { ...newCompany.geolocation, longitude: e.target.value }
                    })}
                  />
                </div>
              </div>

              {/* Business Info */}
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Informations entreprise
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Secteur d'activité</Label>
                    <Select 
                      value={newCompany.sector} 
                      onValueChange={(v) => setNewCompany({...newCompany, sector: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {SECTORS.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre d'employés</Label>
                    <Input
                      type="number"
                      placeholder="ex: 15"
                      value={newCompany.employee_count}
                      onChange={(e) => setNewCompany({ ...newCompany, employee_count: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <UsersRound className="w-4 h-4" />
                  Contact
                </p>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2 col-span-2">
                      <Label>Personne de contact</Label>
                      <Input
                        placeholder="Nom du contact"
                        value={newCompany.contact_person}
                        onChange={(e) => setNewCompany({ ...newCompany, contact_person: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Genre</Label>
                      <Select 
                        value={newCompany.contact_gender} 
                        onValueChange={(v) => setNewCompany({...newCompany, contact_gender: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Genre" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="M">Homme</SelectItem>
                          <SelectItem value="F">Femme</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email de contact</Label>
                    <Input
                      type="email"
                      placeholder="contact@entreprise.com"
                      value={newCompany.contact_email}
                      onChange={(e) => setNewCompany({ ...newCompany, contact_email: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input
                        placeholder="+228 XX XX XX XX"
                        value={newCompany.contact_phone}
                        onChange={(e) => setNewCompany({ ...newCompany, contact_phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>WhatsApp</Label>
                      <Input
                        placeholder="+228 XX XX XX XX"
                        value={newCompany.contact_whatsapp}
                        onChange={(e) => setNewCompany({ ...newCompany, contact_whatsapp: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button 
                onClick={handleCreate} 
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {saving ? 'Création...' : 'Créer l\'entreprise'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Consultant Dialog - With Project Selection */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Attribuer un consultant</DialogTitle>
              <DialogDescription>
                Sélectionnez un projet et un consultant pour <strong>{selectedCompany?.name}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto py-4 space-y-6">
              {/* Step 1: Select Project */}
              <div className="space-y-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <FolderKanban className="w-4 h-4 text-orange-500" />
                  1. Sélectionnez un projet
                </Label>
                {projects.length === 0 ? (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-800">
                      Aucun projet actif. Veuillez d'abord créer un projet dans la section "Projets".
                    </p>
                  </div>
                ) : (
                  <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir un projet..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={
                              project.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50'
                            }>
                              {project.status === 'active' ? 'Actif' : 'Brouillon'}
                            </Badge>
                            {project.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Step 2: Select Consultant (only if project is selected) */}
              {selectedProjectId && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    2. Sélectionnez un consultant
                  </Label>
                  
                  {/* Search filter */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Rechercher un consultant..."
                      value={consultantSearch}
                      onChange={(e) => setConsultantSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-2">
                    {filteredConsultants.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">
                        Aucun consultant trouvé
                      </p>
                    ) : (
                      filteredConsultants.map((consultant) => (
                        <div 
                          key={consultant.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedConsultantId === consultant.id ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:bg-slate-50'
                          }`}
                          onClick={() => setSelectedConsultantId(consultant.id)}
                        >
                          <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                            selectedConsultantId === consultant.id ? 'border-orange-500 bg-orange-500' : 'border-slate-300'
                          }`}>
                            {selectedConsultantId === consultant.id && (
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{consultant.name}</p>
                            <p className="text-sm text-slate-500 truncate">{consultant.email}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Info about existing assignment */}
              {selectedCompany && getCompanyAssignment(selectedCompany.id) && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Attribution existante :</strong> {getCompanyAssignment(selectedCompany.id).consultant_name} 
                    {' '}sur le projet "{getCompanyAssignment(selectedCompany.id).project_name}"
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Une nouvelle attribution sera créée en plus de l'existante.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter className="flex-shrink-0 border-t pt-4">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Annuler</Button>
              <Button 
                onClick={handleAssignConsultant} 
                disabled={saving || !selectedProjectId || !selectedConsultantId}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {saving ? 'Enregistrement...' : 'Créer l\'affectation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Credentials Dialog */}
        <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-green-600" />
                Compte PME créé avec succès
              </DialogTitle>
              <DialogDescription>
                Voici les identifiants de connexion pour {generatedCredentials.companyName}. 
                <span className="text-red-600 font-medium"> Attention : le mot de passe ne sera affiché qu'une seule fois.</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Email</p>
                    <p className="text-slate-900 font-mono">{generatedCredentials.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedCredentials.email)}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Mot de passe</p>
                    <p className="text-slate-900 font-mono font-bold text-lg">{generatedCredentials.password}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedCredentials.password)}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800">
                  💡 Transmettez ces identifiants de manière sécurisée à l'utilisateur PME.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button 
                onClick={() => {
                  copyToClipboard(`Email: ${generatedCredentials.email}\nMot de passe: ${generatedCredentials.password}`);
                }}
                variant="outline"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier tout
              </Button>
              <Button 
                onClick={() => setCredentialsDialogOpen(false)}
                className="bg-slate-900 hover:bg-slate-800"
              >
                J'ai noté les identifiants
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Company Details Dialog (for consultants) */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Détails de la PME
              </DialogTitle>
              <DialogDescription>
                Informations complètes sur {viewingCompany?.name}
              </DialogDescription>
            </DialogHeader>
            {viewingCompany && (
              <div className="flex-1 overflow-y-auto py-4 space-y-4">
                {/* Company Info */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Nom de l'entreprise</p>
                    <p className="text-lg font-semibold text-slate-900">{viewingCompany.name}</p>
                  </div>
                  {viewingCompany.description && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-medium">Description</p>
                      <p className="text-sm text-slate-700">{viewingCompany.description}</p>
                    </div>
                  )}
                  {viewingCompany.sector && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-medium">Secteur d'activité</p>
                      <p className="text-slate-700">{viewingCompany.sector}</p>
                    </div>
                  )}
                  {viewingCompany.employee_count && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-medium">Nombre d'employés</p>
                      <p className="text-slate-700">{viewingCompany.employee_count}</p>
                    </div>
                  )}
                </div>

                {/* Location Info */}
                <div className="p-4 bg-purple-50 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-purple-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Localisation
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {viewingCompany.region && (
                      <div>
                        <p className="text-xs text-purple-600 uppercase">Région</p>
                        <p className="text-slate-700">{viewingCompany.region}</p>
                      </div>
                    )}
                    {viewingCompany.prefecture && (
                      <div>
                        <p className="text-xs text-purple-600 uppercase">Préfecture</p>
                        <p className="text-slate-700">{viewingCompany.prefecture}</p>
                      </div>
                    )}
                    {viewingCompany.commune && (
                      <div>
                        <p className="text-xs text-purple-600 uppercase">Commune</p>
                        <p className="text-slate-700">{viewingCompany.commune}</p>
                      </div>
                    )}
                    {viewingCompany.address && (
                      <div className="col-span-2">
                        <p className="text-xs text-purple-600 uppercase">Adresse</p>
                        <p className="text-slate-700">{viewingCompany.address}</p>
                      </div>
                    )}
                  </div>
                  {viewingCompany.geolocation?.latitude && viewingCompany.geolocation?.longitude && (
                    <div className="pt-2 border-t border-purple-200">
                      <p className="text-xs text-purple-600 uppercase">Coordonnées GPS</p>
                      <p className="text-slate-700 text-sm font-mono">
                        {viewingCompany.geolocation.latitude}, {viewingCompany.geolocation.longitude}
                      </p>
                    </div>
                  )}
                </div>

                {/* Contact Info */}
                <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Contact
                  </p>
                  {viewingCompany.contact_person && (
                    <div>
                      <p className="text-xs text-blue-600 uppercase">Personne de contact</p>
                      <p className="text-slate-700">
                        {viewingCompany.contact_person}
                        {viewingCompany.contact_gender && (
                          <span className="ml-2 text-xs text-slate-500">
                            ({viewingCompany.contact_gender === 'M' ? 'Homme' : 'Femme'})
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  {viewingCompany.contact_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-500" />
                      <div>
                        <p className="text-xs text-blue-600 uppercase">Email</p>
                        <a href={`mailto:${viewingCompany.contact_email}`} className="text-blue-700 hover:underline">
                          {viewingCompany.contact_email}
                        </a>
                      </div>
                    </div>
                  )}
                  {viewingCompany.contact_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-blue-500" />
                      <div>
                        <p className="text-xs text-blue-600 uppercase">Téléphone</p>
                        <a href={`tel:${viewingCompany.contact_phone}`} className="text-blue-700 hover:underline">
                          {viewingCompany.contact_phone}
                        </a>
                      </div>
                    </div>
                  )}
                  {viewingCompany.contact_whatsapp && (
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-green-600" />
                      <div>
                        <p className="text-xs text-green-600 uppercase">WhatsApp</p>
                        <a 
                          href={`https://wa.me/${viewingCompany.contact_whatsapp.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-700 hover:underline"
                        >
                          {viewingCompany.contact_whatsapp}
                        </a>
                      </div>
                    </div>
                  )}
                  {!viewingCompany.contact_person && !viewingCompany.contact_email && !viewingCompany.contact_phone && (
                    <p className="text-slate-500 text-sm">Aucune information de contact disponible</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="border-t pt-4">
              <Button 
                onClick={() => setDetailsDialogOpen(false)}
                className="bg-slate-900 hover:bg-slate-800"
              >
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Report Company Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Signaler un problème
              </DialogTitle>
              <DialogDescription>
                Signaler un problème concernant <strong>{reportingCompany?.name}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Report Type */}
              <div className="space-y-2">
                <Label>Type de signalement *</Label>
                <Select 
                  value={reportForm.report_type} 
                  onValueChange={(v) => setReportForm({...reportForm, report_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez le type de problème" />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description du problème *</Label>
                <Textarea
                  placeholder="Décrivez le problème en détail (ex: Impossible de joindre l'entreprise après plusieurs tentatives...)"
                  value={reportForm.description}
                  onChange={(e) => setReportForm({...reportForm, description: e.target.value})}
                  rows={4}
                  className="resize-none"
                />
              </div>

              {/* Info box */}
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Ce signalement sera transmis à l'administrateur pour vérification et action.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSubmitReport} 
                disabled={submittingReport}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {submittingReport ? (
                  <>Envoi...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer le signalement
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Digitalization Index Dialog */}
        <Dialog open={digitalizationDialogOpen} onOpenChange={setDigitalizationDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                Indice de Digitalisation
              </DialogTitle>
              <DialogDescription>
                Renseigner l&apos;indice de digitalisation pour <strong>{digitalizationCompany?.name}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Show existing indices */}
              {digitalizationCompany && (digitalizationCompany.initial_digitalization_index !== null || digitalizationCompany.final_digitalization_index !== null) && (
                <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                  <p className="text-sm font-medium text-slate-700">Indices actuels :</p>
                  <div className="flex gap-4">
                    <div>
                      <span className="text-xs text-slate-500">Initial : </span>
                      <span className="font-medium text-amber-600">
                        {digitalizationCompany.initial_digitalization_index !== null ? digitalizationCompany.initial_digitalization_index : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-500">Final : </span>
                      <span className="font-medium text-emerald-600">
                        {digitalizationCompany.final_digitalization_index !== null ? digitalizationCompany.final_digitalization_index : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Type selection */}
              <div className="space-y-2">
                <Label>Type d&apos;indice *</Label>
                <Select 
                  value={digitalizationForm.index_type} 
                  onValueChange={(v) => setDigitalizationForm({...digitalizationForm, index_type: v})}
                >
                  <SelectTrigger data-testid="digitalization-type">
                    <SelectValue placeholder="Sélectionnez le type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="initial">
                      Indice Initial (début de mission)
                    </SelectItem>
                    <SelectItem 
                      value="final"
                      disabled={digitalizationCompany?.initial_digitalization_index === null || digitalizationCompany?.initial_digitalization_index === undefined}
                    >
                      Indice Final (fin de mission)
                      {(digitalizationCompany?.initial_digitalization_index === null || digitalizationCompany?.initial_digitalization_index === undefined) && 
                        " - Renseigner d'abord l'initial"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Value input */}
              <div className="space-y-2">
                <Label>Valeur de l&apos;indice (0-100) *</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="Ex: 45.5"
                  value={digitalizationForm.value}
                  onChange={(e) => setDigitalizationForm({...digitalizationForm, value: e.target.value})}
                  data-testid="digitalization-value"
                />
                <p className="text-xs text-slate-500">
                  0 = Aucune digitalisation, 100 = Digitalisation complète
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setDigitalizationDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button 
                onClick={handleSubmitDigitalization}
                disabled={submittingDigitalization || !digitalizationForm.value}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {submittingDigitalization ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
